/**
 * Layout transform.
 *
 * Computes the chart grid (for axis-based charts) or series positioning
 * (for pie/funnel) so that legend, labels, axis titles, and data never
 * overlap.  Runs **last** in the pipeline so it can inspect the final
 * state of every other transform.
 *
 * **Data-aware estimation** (single-pass):
 *   Instead of using static constants, we measure the actual legend item
 *   texts (series names / category names) using an offscreen canvas to
 *   predict how many rows/columns the legend will occupy.  This gives
 *   near-pixel-perfect spacing without a costly two-pass render.
 *
 * Key responsibilities:
 *   • Reserve space for visible legend at any edge
 *   • Reserve space for outside data labels
 *   • Keep containLabel = true so ECharts axes auto-fit
 *   • Adjust pie/funnel center + radius to avoid legend overlap
 */
import type { EChartsOption } from 'echarts';
import type { ChartStyleState } from '../../../../edit/visualConfig';
import { getCapabilities } from '../../../../edit/visualConfig';
import { isSingleObject } from './helpers';
import { measureText, maxLabelWidth } from '../utils';

// ── Layout context ─────────────────────────────────────────────────────────

export interface LayoutContext {
  containerWidth: number;
  containerHeight: number;
}

// ── Constants ──────────────────────────────────────────────────────────────

/** Base grid padding for axis-based charts (px). */
const BASE_TOP = 16;
const BASE_BOTTOM = 16;
const BASE_LEFT = 16;
const BASE_RIGHT = 16;

/** Per-row height for horizontal legend items (px). */
const LEGEND_ROW_HEIGHT = 22;
/** Vertical padding around the legend block (px). */
const LEGEND_V_PAD = 8;
/** Horizontal gap per legend item (icon + gap + text + spacing). */
const LEGEND_ITEM_EXTRA = 34;
/** Horizontal padding the legend reserves on each side. */
const LEGEND_H_PAD = 64;
/** Minimum extra width for vertical legend (icon + gap). */
const LEGEND_V_ICON_GAP = 30;

/** Extra top padding when data labels extend above chart area. */
const LABEL_TOP_RESERVE = 20;

/** Legend font used for text measurement — matches theme.ts. */
const LEGEND_FONT = '12px -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif';

// ── Legend measurement helpers ─────────────────────────────────────────────

/**
 * Extract the legend item names from the option.
 * For pie/funnel the items are data names; for axis charts they're series names.
 */
function getLegendItems(option: EChartsOption): string[] {
  // If legend.data is explicitly set, use it
  const leg: any = Array.isArray(option.legend) ? option.legend[0] : option.legend;
  if (leg?.data && Array.isArray(leg.data)) {
    return (leg.data as any[]).map((d: any) =>
      typeof d === 'string' ? d : (d?.name ?? String(d)),
    );
  }

  if (!Array.isArray(option.series)) return [];

  const seriesArr = option.series as any[];
  // Pie/funnel: items come from data[].name
  if (seriesArr.length === 1 && (seriesArr[0].type === 'pie' || seriesArr[0].type === 'funnel')) {
    const data = seriesArr[0].data;
    if (Array.isArray(data)) {
      return data.map((d: any) => String(d?.name ?? ''));
    }
  }

  // Axis charts: items are series names
  return seriesArr
    .map((s: any) => s.name as string | undefined)
    .filter((n): n is string => !!n && n !== '__total__');
}

/**
 * Estimate the pixel height of a horizontal legend.
 * Measures each item's text width, lays them out left-to-right, and
 * counts how many rows they wrap into given the available width.
 */
function estimateHorizontalLegendHeight(items: string[], containerWidth: number): number {
  if (items.length === 0) return 0;

  const availableWidth = Math.max(containerWidth - LEGEND_H_PAD, 100);
  let currentRowWidth = 0;
  let rows = 1;

  for (const name of items) {
    const itemWidth = measureText(name, LEGEND_FONT) + LEGEND_ITEM_EXTRA;
    if (currentRowWidth + itemWidth > availableWidth && currentRowWidth > 0) {
      rows++;
      currentRowWidth = itemWidth;
    } else {
      currentRowWidth += itemWidth;
    }
  }

  return rows * LEGEND_ROW_HEIGHT + LEGEND_V_PAD;
}

/**
 * Estimate the pixel width of a vertical legend.
 * Finds the widest item text and adds icon/gap space.
 */
function estimateVerticalLegendWidth(items: string[]): number {
  if (items.length === 0) return 0;
  return maxLabelWidth(items, LEGEND_FONT) + LEGEND_V_ICON_GAP + 16;
}

// ── Other helpers ──────────────────────────────────────────────────────────

/** True if the legend uses scroll mode (single row/column with arrows). */
function isScrollLegend(option: EChartsOption): boolean {
  const leg: any = Array.isArray(option.legend) ? option.legend[0] : option.legend;
  return leg?.type === 'scroll';
}

/** True if the legend is visible in the final option. */
function isLegendVisible(option: EChartsOption): boolean {
  const leg: any = Array.isArray(option.legend) ? option.legend[0] : option.legend;
  return leg?.show === true;
}

/** True if data labels extend above the chart area. */
function labelsExtendTop(vc: ChartStyleState): boolean {
  if (!vc.showLabels) return false;
  return vc.labelPosition === 'outside';
}

/** Count data items in pie/funnel series for radius estimation. */
function getRadialDataCount(option: EChartsOption): number {
  if (!Array.isArray(option.series)) return 0;
  for (const s of option.series as any[]) {
    if ((s.type === 'pie' || s.type === 'funnel') && Array.isArray(s.data)) {
      return s.data.length;
    }
  }
  return 0;
}

// ── Axis-chart layout ──────────────────────────────────────────────────────

function applyAxisLayout(
  option: EChartsOption,
  vc: ChartStyleState,
  ctx: LayoutContext,
): EChartsOption {
  const result = { ...option };

  let top = BASE_TOP;
  let bottom = BASE_BOTTOM;
  let left = BASE_LEFT;
  let right = BASE_RIGHT;

  const legendVisible = isLegendVisible(option);
  const pos = vc.legendPosition ?? 'top';

  if (legendVisible) {
    const items = getLegendItems(option);
    const scrollH = isScrollLegend(option);
    switch (pos) {
      case 'top':
        top += scrollH
          ? LEGEND_ROW_HEIGHT + LEGEND_V_PAD
          : estimateHorizontalLegendHeight(items, ctx.containerWidth);
        break;
      case 'bottom':
        bottom += scrollH
          ? LEGEND_ROW_HEIGHT + LEGEND_V_PAD
          : estimateHorizontalLegendHeight(items, ctx.containerWidth);
        break;
      case 'left':
        left += estimateVerticalLegendWidth(items);
        break;
      case 'right':
        right += estimateVerticalLegendWidth(items);
        break;
    }
  }

  if (labelsExtendTop(vc)) {
    top += LABEL_TOP_RESERVE;
  }

  // Axis names are positioned outside containLabel bounds — reserve space
  // dynamically based on the actual nameGap set by the axis transform.
  if (isSingleObject(result.xAxis)) {
    const xa = result.xAxis as any;
    if (xa.name) {
      const nameGap = xa.nameGap ?? 40;
      // nameGap measures from axis line; we need extra grid bottom so
      // the name text isn't clipped by the container edge.
      bottom += Math.max(nameGap - 16, 14);
    }
  }
  if (isSingleObject(result.yAxis)) {
    const ya = result.yAxis as any;
    if (ya.name) {
      const nameGap = ya.nameGap ?? 60;
      // nameGap is the distance from the axis line to the title center.
      // containLabel already reserves space for tick labels.  We need
      // extra grid.left so the title text isn't clipped at the edge.
      // Subtract ~40px (typical containLabel tick-label reservation)
      // and add a minimum of 14px.
      left += Math.max(nameGap - 40, 14);
    }
  }

  // Merge onto existing grid (chart-type overrides like heatmap may
  // have already set custom values we should respect as minimums).
  if (isSingleObject(result.grid)) {
    const g = result.grid as any;
    g.containLabel = true;
    g.top = Math.max(parseInt(g.top ?? 0, 10) || 0, top);
    g.bottom = Math.max(parseInt(g.bottom ?? 0, 10) || 0, bottom);
    g.left = Math.max(parseInt(g.left ?? 0, 10) || 0, left);
    g.right = Math.max(parseInt(g.right ?? 0, 10) || 0, right);
  } else {
    result.grid = { containLabel: true, top, bottom, left, right };
  }

  return result;
}

// ── Pie / Funnel layout ────────────────────────────────────────────────────

function applyRadialLayout(
  option: EChartsOption,
  vc: ChartStyleState,
  ctx: LayoutContext,
): EChartsOption {
  if (!Array.isArray(option.series)) return option;

  const result = { ...option };
  const legendVisible = isLegendVisible(option);
  const pos = vc.legendPosition ?? 'top';

  // Start centred with generous radius
  let cx = '50%';
  let cy = '50%';
  let maxRadius = 70; // percent of container

  if (legendVisible) {
    const items = getLegendItems(option);

    const scrollR = isScrollLegend(option);
    switch (pos) {
      case 'top': {
        const h = scrollR
          ? LEGEND_ROW_HEIGHT + LEGEND_V_PAD
          : estimateHorizontalLegendHeight(items, ctx.containerWidth);
        // Shift centre down proportionally and shrink radius
        const shiftPct = Math.round((h / ctx.containerHeight) * 50);
        cy = `${50 + Math.min(shiftPct, 12)}%`;
        maxRadius = Math.max(70 - Math.round((h / ctx.containerHeight) * 30), 45);
        break;
      }
      case 'bottom': {
        const h = scrollR
          ? LEGEND_ROW_HEIGHT + LEGEND_V_PAD
          : estimateHorizontalLegendHeight(items, ctx.containerWidth);
        const shiftPct = Math.round((h / ctx.containerHeight) * 50);
        cy = `${50 - Math.min(shiftPct, 12)}%`;
        maxRadius = Math.max(70 - Math.round((h / ctx.containerHeight) * 30), 45);
        break;
      }
      case 'left': {
        const w = estimateVerticalLegendWidth(items);
        const shiftPct = Math.round((w / ctx.containerWidth) * 50);
        cx = `${50 + Math.min(shiftPct, 12)}%`;
        maxRadius = Math.max(70 - Math.round((w / ctx.containerWidth) * 30), 40);
        break;
      }
      case 'right': {
        const w = estimateVerticalLegendWidth(items);
        const shiftPct = Math.round((w / ctx.containerWidth) * 50);
        cx = `${50 - Math.min(shiftPct, 12)}%`;
        maxRadius = Math.max(70 - Math.round((w / ctx.containerWidth) * 30), 40);
        break;
      }
    }
  }

  // Outside labels: shrink radius based on item count so labels have room
  if (vc.labelPosition === 'outside') {
    const dataCount = getRadialDataCount(option);
    if (dataCount > 12) {
      maxRadius = Math.min(maxRadius, 45);
    } else if (dataCount > 6) {
      maxRadius = Math.min(maxRadius, 52);
    } else {
      maxRadius = Math.min(maxRadius, 58);
    }
  }

  result.series = (result.series as any[]).map((s: any) => {
    if (s.type === 'pie') {
      // Respect user's explicit outerRadius — parse from the existing
      // radius string set by chartOverrides (e.g. "60%") and cap it to
      // maxRadius so it doesn't overflow the legend/label space.
      let outerPct = maxRadius;
      if (Array.isArray(s.radius) && typeof s.radius[1] === 'string') {
        const parsed = parseInt(s.radius[1], 10);
        if (!isNaN(parsed) && parsed > 0) {
          outerPct = Math.min(parsed, maxRadius);
        }
      }
      // Preserve inner radius for donut charts, but ensure it stays < outer
      let innerPct = 0;
      if (Array.isArray(s.radius) && typeof s.radius[0] === 'string') {
        const parsed = parseInt(s.radius[0], 10);
        if (!isNaN(parsed) && parsed > 0) {
          innerPct = Math.min(parsed, outerPct - 5);
        }
      }
      return {
        ...s,
        center: [cx, cy],
        radius: [`${Math.max(innerPct, 0)}%`, `${outerPct}%`],
      };
    }
    if (s.type === 'funnel') {
      // Funnel uses top/bottom/left/width positioning
      let fTop = 16;
      let fBottom = 16;
      let fLeft = '10%';
      let fWidth = '80%';

      if (legendVisible) {
        const items = getLegendItems(option);
        const scrollF = isScrollLegend(option);
        switch (pos) {
          case 'top':
            fTop = (scrollF
              ? LEGEND_ROW_HEIGHT + LEGEND_V_PAD
              : estimateHorizontalLegendHeight(items, ctx.containerWidth)) + 8;
            break;
          case 'bottom':
            fBottom = (scrollF
              ? LEGEND_ROW_HEIGHT + LEGEND_V_PAD
              : estimateHorizontalLegendHeight(items, ctx.containerWidth)) + 8;
            break;
          case 'left': {
            const lw = estimateVerticalLegendWidth(items);
            const pct = Math.round((lw / ctx.containerWidth) * 100) + 2;
            fLeft = `${pct}%`;
            fWidth = `${Math.max(80 - pct, 50)}%`;
            break;
          }
          case 'right': {
            const rw = estimateVerticalLegendWidth(items);
            const pct = Math.round((rw / ctx.containerWidth) * 100) + 2;
            fWidth = `${Math.max(80 - pct, 50)}%`;
            break;
          }
        }
      }

      return {
        ...s,
        top: fTop,
        bottom: fBottom,
        left: fLeft,
        width: fWidth,
      };
    }
    return s;
  });

  return result;
}

// ── Public API ─────────────────────────────────────────────────────────────

/**
 * Compute chart layout spacing.
 *
 * Uses data-aware text measurement (single-pass) to estimate legend
 * dimensions and adjust grid/center/radius accordingly.
 *
 * @param option          Final ECharts option (after all other transforms)
 * @param vc              Current ChartStyleState
 * @param containerWidth  Container width in px (from ResizeObserver)
 * @param containerHeight Container height in px
 */
export function applyLayout(
  option: EChartsOption,
  vc: ChartStyleState,
  containerWidth: number = 400,
  containerHeight: number = 300,
  chartType?: string,
): EChartsOption {
  const caps = getCapabilities((chartType ?? 'bar') as any);
  const ctx: LayoutContext = { containerWidth, containerHeight };

  if (caps.axis) {
    return applyAxisLayout(option, vc, ctx);
  }

  // Non-axis charts: pie, funnel
  return applyRadialLayout(option, vc, ctx);
}